import type { FC, MouseEvent, ReactNode } from 'react'

import { useEffect, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { cn } from '@/lib/utils'

import {
    BadgeCheck,
    CalendarCheck,
    Check,
    Flag,
    IndianRupee,
    Megaphone,
    Users,
    Wallet,
    Wrench
} from 'lucide-react'

import { PHASES, PIONEER_ROLES, READINESS_SKILLS } from '@/constants/campus'

const ease = [0.22, 1, 0.36, 1] as const

// A dark card with a soft spotlight that follows the cursor.
const BentoCard: FC<{ className?: string, delay?: number, children: ReactNode }> = ({ className, delay = 0, children }): ReactNode => {
    const onMove = (event: MouseEvent<HTMLDivElement>) => {
        const rect = event.currentTarget.getBoundingClientRect()

        event.currentTarget.style.setProperty('--spot-x', `${event.clientX - rect.left}px`)
        event.currentTarget.style.setProperty('--spot-y', `${event.clientY - rect.top}px`)
    }

    return (
        <motion.article
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.7, delay, ease }}
            onMouseMove={onMove}
            className={cn(
                'group relative overflow-hidden rounded-3xl border border-white/10 bg-midnight-card p-7 transition-colors duration-300 hover:border-indigo-500/40 sm:p-8',
                className
            )}
        >
            <div
                aria-hidden='true'
                className='pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100'
                style={{ background: 'radial-gradient(420px circle at var(--spot-x, 50%) var(--spot-y, 50%), rgba(99,102,241,0.14), transparent 60%)' }}
            />

            <div className='relative flex h-full flex-col'>
                {children}
            </div>
        </motion.article>
    )
}

const CardText: FC<{ eyebrow: string, title: string, body: string }> = ({ eyebrow, title, body }): ReactNode => (
    <div>
        <p className='text-[11px] font-bold uppercase tracking-[0.16em] text-indigo-300'>{eyebrow}</p>
        <h3 className='mt-3 text-2xl font-semibold tracking-tight text-white'>{title}</h3>
        <p className='mt-3 max-w-lg text-[15px] leading-relaxed text-slate-400'>{body}</p>
    </div>
)

const JourneyIllustration: FC = (): ReactNode => {
    const lanes = [
        { label: "CSE", color: '#6366F1' },
        { label: "ECE", color: '#D946EF' },
        { label: "EE", color: '#F59E0B' },
        { label: "ME", color: '#10B981' }
    ]

    return (
        <div className='mt-8 rounded-2xl border border-white/10 bg-midnight/60 p-5'>
            <div className='grid grid-cols-[repeat(32,minmax(0,1fr))] gap-[3px]'>
                {Array.from({ length: 32 }, (_, index) => {
                    const month = index + 1
                    const phase = PHASES.find(item => month >= item.from && month <= item.to) ?? PHASES[0]
                    const milestone = [9, 18, 29, 32].includes(month)

                    return (
                        <motion.span
                            key={month}
                            initial={{ opacity: 0, y: 8 }}
                            whileInView={{ opacity: milestone ? 1 : 0.55, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: index * 0.02, duration: 0.4 }}
                            className={cn('relative h-9 rounded-[4px]', milestone && 'ring-2 ring-amber-400 ring-offset-2 ring-offset-midnight')}
                            style={{ backgroundColor: phase.color }}
                        />
                    )
                })}
            </div>

            <div className='mt-2 flex justify-between text-[10px] font-semibold text-slate-500'>
                <span>Month 1</span>
                <span className='inline-flex items-center gap-1 text-amber-300'><Flag className='size-3' />Seasonal milestones</span>
                <span>Month 32</span>
            </div>

            <div className='mt-5 space-y-2.5'>
                {lanes.map((lane, index) => (
                    <div key={lane.label} className='flex items-center gap-3'>
                        <span className='w-9 text-[11px] font-bold text-slate-400'>{lane.label}</span>

                        <div className='relative h-2 flex-1 overflow-hidden rounded-full bg-white/[0.06]'>
                            <motion.span
                                className='absolute top-0 h-full w-1/4 rounded-full'
                                style={{ backgroundColor: lane.color }}
                                animate={{ left: ['-25%', '100%'] }}
                                transition={{ duration: 6, repeat: Infinity, ease: 'linear', delay: index * 1.5 }}
                            />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    )
}

const PricingIllustration: FC = (): ReactNode => (
    <div className='mt-8 flex flex-1 flex-col justify-end'>
        <div className='rounded-2xl border border-white/10 bg-midnight/60 p-5'>
            <div className='flex items-center gap-3 font-mono text-sm text-slate-400'>
                <span className='rounded-lg bg-white/[0.06] px-2.5 py-1 text-white'>₹399</span>
                <span>÷</span>
                <span className='rounded-lg bg-white/[0.06] px-2.5 py-1 text-white'>4 years</span>
                <span>=</span>
            </div>

            <div className='mt-4 flex items-end gap-2'>
                <span className='bg-gradient-to-r from-emerald-300 to-sky-300 bg-clip-text text-6xl font-bold tracking-tight text-transparent'>₹99.75</span>
                <span className='pb-2 text-sm font-medium text-slate-400'>/ year</span>
            </div>

            <div className='mt-5 flex items-center gap-2'>
                <span className='inline-flex items-center gap-1.5 rounded-full bg-emerald-400/10 px-3 py-1 text-xs font-semibold text-emerald-300 ring-1 ring-emerald-400/20'>
                    <IndianRupee className='size-3.5' />
                    Less than ₹100 a year
                </span>

                <span className='inline-flex items-center gap-1.5 rounded-full bg-white/[0.06] px-3 py-1 text-xs font-semibold text-slate-300'>
                    <Wallet className='size-3.5' />
                    100% accounted for
                </span>
            </div>
        </div>
    </div>
)

const RADAR_SCORES = [84, 78, 88, 70, 82]

const RadarIllustration: FC = (): ReactNode => {
    const center = 110
    const radius = 82
    const point = (index: number, scale: number) => {
        const angle = -Math.PI / 2 + (index * 2 * Math.PI) / 5

        // Rounded so the server and every browser print identical coordinates.
        return [Math.round((center + Math.cos(angle) * radius * scale) * 10) / 10, Math.round((center + Math.sin(angle) * radius * scale) * 10) / 10]
    }

    const ring = (scale: number) => Array.from({ length: 5 }, (_, index) => point(index, scale).map(value => value.toFixed(1)).join(',')).join(' ')
    const shape = RADAR_SCORES.map((score, index) => point(index, score / 100).map(value => value.toFixed(1)).join(',')).join(' ')

    return (
        <div className='mt-6 flex flex-1 flex-col items-center justify-end gap-4 sm:flex-row sm:items-end sm:justify-between'>
            <svg viewBox='0 0 220 220' className='w-full max-w-[240px]' role='img' aria-label='Five-skill readiness radar chart'>
                {[0.25, 0.5, 0.75, 1].map(scale => (
                    <polygon key={scale} points={ring(scale)} fill='none' stroke='rgba(255,255,255,0.09)' strokeWidth='1' />
                ))}

                {Array.from({ length: 5 }, (_, index) => {
                    const [x, y] = point(index, 1)

                    return <line key={index} x1={center} y1={center} x2={x} y2={y} stroke='rgba(255,255,255,0.08)' />
                })}

                <motion.polygon
                    points={shape}
                    fill='rgba(99,102,241,0.28)'
                    stroke='#818CF8'
                    strokeWidth='2'
                    initial={{ scale: 0.2, opacity: 0 }}
                    whileInView={{ scale: 1, opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1, ease }}
                    style={{ originX: '50%', originY: '50%' }}
                />

                {RADAR_SCORES.map((score, index) => {
                    const [x, y] = point(index, score / 100)

                    return <circle key={index} cx={x} cy={y} r='3.5' fill='#A5B4FC' />
                })}
            </svg>

            <div className='w-full max-w-[200px] space-y-2'>
                <div className='flex items-baseline gap-1'>
                    <span className='text-5xl font-bold tracking-tight text-white'>82</span>
                    <span className='text-sm text-slate-400'>/ 100</span>
                </div>

                {READINESS_SKILLS.map((skill, index) => (
                    <div key={skill} className='flex items-center justify-between gap-3 text-xs'>
                        <span className='text-slate-400'>{skill}</span>
                        <span className='font-mono font-semibold text-slate-200'>{RADAR_SCORES[index]}</span>
                    </div>
                ))}
            </div>
        </div>
    )
}

const FEED = [
    { icon: CalendarCheck, title: "Drive request · Vertex Robotics", status: "Approved by T&P", tone: 'text-emerald-300 bg-emerald-400/10' },
    { icon: Megaphone, title: "Event published · Circuit Troubleshooting", status: "Marketing Lead", tone: 'text-sky-300 bg-sky-400/10' },
    { icon: BadgeCheck, title: "42 certificates issued", status: "QR-verified", tone: 'text-amber-300 bg-amber-400/10' },
    { icon: Wallet, title: "Sponsor confirmed · CodeSprint", status: "Treasurer", tone: 'text-fuchsia-300 bg-fuchsia-400/10' }
]

const WorkflowIllustration: FC = (): ReactNode => {
    const [offset, setOffset] = useState(0)

    useEffect(() => {
        const timer = window.setInterval(() => setOffset(value => (value + 1) % FEED.length), 2600)

        return () => window.clearInterval(timer)
    }, [])

    const visible = [0, 1, 2].map(step => FEED[(offset + step) % FEED.length])

    return (
        <div className='mt-8 grid flex-1 gap-4 sm:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]'>
            <div className='rounded-2xl border border-white/10 bg-midnight/60 p-5'>
                <div className='flex items-center justify-between'>
                    <p className='text-xs font-semibold text-slate-400'>Future Pioneer licences</p>
                    <span className='font-mono text-xs font-bold text-white'>10 / 10</span>
                </div>

                <div className='mt-3 grid grid-cols-10 gap-1'>
                    {Array.from({ length: 10 }, (_, index) => (
                        <motion.span
                            key={index}
                            initial={{ scale: 0 }}
                            whileInView={{ scale: 1 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.2 + index * 0.05, type: 'spring', stiffness: 400, damping: 18 }}
                            className='aspect-square rounded-md bg-gradient-to-br from-indigo-400 to-emerald-400'
                        />
                    ))}
                </div>

                <div className='mt-4 flex flex-wrap gap-1.5'>
                    {PIONEER_ROLES.map(role => (
                        <span key={role} className='inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/[0.04] px-2.5 py-1 text-[11px] font-medium text-slate-300'>
                            {role === 'Technical Head' ? <Wrench className='size-3' /> : <Users className='size-3' />}
                            {role}
                        </span>
                    ))}
                </div>
            </div>

            <div className='rounded-2xl border border-white/10 bg-midnight/60 p-5'>
                <div className='flex items-center justify-between'>
                    <p className='text-xs font-semibold text-slate-400'>Live approvals</p>

                    <span className='inline-flex items-center gap-1.5 text-[11px] font-semibold text-emerald-300'>
                        <span className='size-1.5 animate-pulse rounded-full bg-emerald-400' />
                        Real time
                    </span>
                </div>

                <ul className='mt-3 space-y-2'>
                    <AnimatePresence initial={false} mode='popLayout'>
                        {visible.map(item => (
                            <motion.li
                                key={item.title}
                                layout
                                initial={{ opacity: 0, y: -12 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: 12 }}
                                transition={{ duration: 0.4, ease }}
                                className='flex items-center gap-3 rounded-xl bg-white/[0.04] p-2.5 ring-1 ring-white/[0.06]'
                            >
                                <span className={cn('grid size-8 shrink-0 place-items-center rounded-lg', item.tone)}>
                                    <item.icon className='size-4' />
                                </span>

                                <span className='min-w-0 flex-1'>
                                    <span className='block truncate text-[13px] font-medium text-white'>{item.title}</span>
                                    <span className='block text-[11px] text-slate-400'>{item.status}</span>
                                </span>

                                <Check className='size-4 shrink-0 text-emerald-400' />
                            </motion.li>
                        ))}
                    </AnimatePresence>
                </ul>
            </div>
        </div>
    )
}

export const Bento: FC = (): ReactNode => {
    return (
        <section id='why' aria-labelledby='why-title' className='relative overflow-hidden bg-midnight px-5 py-24 sm:px-6 sm:py-28'>
            <div aria-hidden='true' className='pointer-events-none absolute inset-0'>
                <div className='absolute inset-0 bg-grid-dark opacity-60 fade-mask-radial' />
                <div className='absolute left-1/2 top-0 h-72 w-[800px] -translate-x-1/2 rounded-full bg-indigo-600/15 blur-[110px]' />
            </div>

            <div className='relative mx-auto max-w-7xl'>
                <div className='mx-auto max-w-3xl text-center'>
                    <motion.p
                        initial={{ opacity: 0, y: 12 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className='text-[11px] font-bold uppercase tracking-[0.18em] text-emerald-300'
                    >
                        Why GKV Tech Campus
                    </motion.p>

                    <motion.h2
                        id='why-title'
                        initial={{ opacity: 0, y: 24, filter: 'blur(8px)' }}
                        whileInView={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, ease }}
                        className='mt-4 text-balance text-4xl font-extrabold leading-[1.1] tracking-tight text-white sm:text-5xl'
                    >
                        Simplicity meets <span className='font-serif font-normal italic text-indigo-200'>excellence</span>, our platform excels in every aspect
                    </motion.h2>
                </div>

                <div className='mt-14 grid gap-5 lg:grid-cols-12'>
                    <BentoCard className='lg:col-span-7'>
                        <CardText
                            eyebrow="Continuous 32-Month Innovation"
                            title="A challenge every week, for every branch"
                            body="Weekly rotational challenges across all 4 engineering branches so no student is left behind from 1st year to final year."
                        />

                        <JourneyIllustration />
                    </BentoCard>

                    <BentoCard className='lg:col-span-5' delay={0.08}>
                        <CardText
                            eyebrow="Student-Friendly Transparent Pricing"
                            title="Subsidized campus plans"
                            body="Less than ₹100 a year powers your entire placement and skill journey on the 4-Year Complete Pass."
                        />

                        <PricingIllustration />
                    </BentoCard>

                    <BentoCard className='lg:col-span-5' delay={0.04}>
                        <CardText
                            eyebrow="Evidence-Based 5-Skill Readiness"
                            title="Scores backed by real records"
                            body="T&P Officers rate Aptitude, Coding / Core, Problem Solving, Communication and Practical Innovation, combined with verified attendance and hackathon scores."
                        />

                        <RadarIllustration />
                    </BentoCard>

                    <BentoCard className='lg:col-span-7' delay={0.12}>
                        <CardText
                            eyebrow="Zero-Friction T&P & Coordinator Workflow"
                            title="Every role gets its own toolkit"
                            body="Role-specific toolkits for the 10 Future Pioneers (Event Manager, Treasurer, Marketing Lead, Technical Head, Community Manager) and real-time T&P approvals."
                        />

                        <WorkflowIllustration />
                    </BentoCard>
                </div>
            </div>
        </section>
    )
}
